import update from 'immutability-helper';

import generateUniqueId from '../../../shared/utils/generateUniqueId';

import {
  FETCH_EXERCISES_FULFILLED,
  FETCH_EXERCISES_REJECTED,
  FETCH_GROUPS_FULFILLED,
  FETCH_GROUPS_REJECTED,
  FETCH_LEADERBOARD_START,
  FETCH_LEADERBOARD_FULFILLED,
  FETCH_LEADERBOARD_REJECTED,
  FETCH_NEXT_PAGE_FULFILLED,
  FETCH_NEXT_PAGE_START,
  FETCH_NEXT_PAGE_REJECTED,
  EDIT_LEADERBOARD_START,
  EDIT_LEADERBOARD_FULFILLED,
  STASH_PENDING_RELOAD,
  APPLY_PENDING_RELOAD,
  CLOSE_BOARD,
  SET_MODAL_OPEN,
  UPDATE_SETTING,
  SET_LEADERBOARD_VIEW,
  FETCH_TEMPLATES_FULFILLED,
  SAVE_TEMPLATE_START,
  SAVE_TEMPLATE_FULFILLED,
  SAVE_TEMPLATE_REJECTED,
  UPDATE_TEMPLATE_START,
  UPDATE_TEMPLATE_FULFILLED,
  UPDATE_TEMPLATE_REJECTED,
  DELETE_TEMPLATE_FULFILLED,
  MARK_BOARD_AS_TEMPLATE,
  CLEAR_ALL_BOARDS,
} from './actionTypes';

const getErrorMessage = (payload) => (
  payload?.response?.status === 403
    ? null // 403 handled by toast in action creator
    : payload?.response?.headers?.message
      || 'An unknown error occurred. Please try again or contact support if the issue persists.'
);

const initialState = {
  exercises: [],
  exercisesError: '',
  groups: [],
  groupsError: '',
  leaderboardError: null,
  leaderboards: {},
  leaderboardsReference: {},
  saveTemplateError: null,
  templates: [],
  updateTemplateError: null,
};

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case FETCH_EXERCISES_FULFILLED: {
      return {
        ...state,
        exercises: action.payload,
      };
    }
    case FETCH_EXERCISES_REJECTED: {
      return {
        ...state,
        exercisesError: getErrorMessage(action.payload),
      };
    }
    case FETCH_GROUPS_FULFILLED: {
      return {
        ...state,
        groups: action.payload,
      };
    }
    case FETCH_GROUPS_REJECTED: {
      return {
        ...state,
        groupsError: getErrorMessage(action.payload),
      };
    }
    case FETCH_LEADERBOARD_FULFILLED: {
      const leaderboardId = generateUniqueId();
      return update(state, {
        leaderboards: {
          $merge: {
            [leaderboardId]: {
              id: leaderboardId,
              leaderboardData: action.payload.data,
              leaderboardExercise: action.payload.exerciseName,
              leaderboardForm: action.payload.values,
              isTemplate: action.payload.isTemplate || false,
              templateId: action.payload.templateId || null,
              page: 1,
              pendingReloadData: null,
              settings: {
                autoScroll: false,
                autoLoad: false,
                loadTime: { value: 60000, label: 'One Minute' },
              },
            },
          },
        },
        leaderboardsReference: {
          $merge: {
            [leaderboardId]: {
              id: leaderboardId,
            },
          },
        },
      });
    }
    case EDIT_LEADERBOARD_FULFILLED: {
      return update(state, {
        leaderboards: {
          [action.payload.id]: {
            $merge: {
              leaderboardData: action.payload.data,
              leaderboardExercise: action.payload.exerciseName,
              leaderboardForm: action.payload.values,
              page: 1,
              isEndOfList: false,
              // A real edit supersedes any reload data staged before it was submitted.
              pendingReloadData: null,
            },
          },
        },
      });
    }
    case STASH_PENDING_RELOAD: {
      return update(state, {
        leaderboards: {
          [action.payload.id]: {
            pendingReloadData: {
              $set: {
                data: action.payload.data,
                exerciseName: action.payload.exerciseName,
                values: action.payload.values,
                page: action.payload.page,
                isEndOfList: action.payload.isEndOfList,
              },
            },
          },
        },
      });
    }
    case APPLY_PENDING_RELOAD: {
      const pending = state.leaderboards[action.payload.id]?.pendingReloadData;
      if (!pending) {
        return state;
      }
      return update(state, {
        leaderboards: {
          [action.payload.id]: {
            $merge: {
              leaderboardData: pending.data,
              leaderboardExercise: pending.exerciseName,
              leaderboardForm: pending.values,
              page: pending.page,
              isEndOfList: pending.isEndOfList,
              pendingReloadData: null,
            },
          },
        },
      });
    }
    case FETCH_LEADERBOARD_START:
    case EDIT_LEADERBOARD_START: {
      return { ...state, leaderboardError: null };
    }
    case FETCH_LEADERBOARD_REJECTED: {
      return {
        ...state,
        leaderboardError: getErrorMessage(action.payload),
      };
    }
    case FETCH_NEXT_PAGE_START: {
      return update(state, {
        leaderboards: {
          [action.payload.id]: {
            isLeaderboardLoading: {
              $set: true,
            },
          },
        },
      });
    }
    case FETCH_NEXT_PAGE_FULFILLED: {
      return update(state, {
        leaderboards: {
          [action.payload.id]: {
            leaderboardData: {
              $push: action.payload.page === state.leaderboards[action.payload.id].page
                ? []
                : action.payload.data,
            },
            page: {
              $set: action.payload.page,
            },
            isEndOfList: {
              $set: action.payload.data.length === 0,
            },
            isLeaderboardLoading: {
              $set: false,
            },
          },
        },
      });
    }
    case FETCH_NEXT_PAGE_REJECTED: {
      return update(state, {
        leaderboards: {
          [action.payload.id]: {
            isLeaderboardLoading: {
              $set: false,
            },
          },
        },
      });
    }
    case CLOSE_BOARD: {
      return update(state, {
        leaderboards: {
          $unset: [action.payload.id],
        },
        leaderboardsReference: {
          $unset: [action.payload.id],
        },
      });
    }
    case SET_MODAL_OPEN: {
      return {
        ...state,
        leaderboardError: null,
        saveTemplateError: null,
        updateTemplateError: null,
      };
    }
    case UPDATE_SETTING: {
      return update(state, {
        leaderboards: {
          [action.payload.id]: {
            settings: {
              [action.payload.setting]: {
                $set: action.payload.value,
              },
            },
          },
        },
      });
    }
    case FETCH_TEMPLATES_FULFILLED: {
      return {
        ...state,
        templates: [...action.payload].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)),
      };
    }
    case SAVE_TEMPLATE_START: {
      return { ...state, saveTemplateError: null };
    }
    case SAVE_TEMPLATE_FULFILLED: {
      return {
        ...state,
        saveTemplateError: null,
        templates: [action.payload, ...state.templates],
      };
    }
    case SAVE_TEMPLATE_REJECTED: {
      return { ...state, saveTemplateError: getErrorMessage(action.payload) };
    }
    case UPDATE_TEMPLATE_START: {
      return { ...state, updateTemplateError: null };
    }
    case UPDATE_TEMPLATE_FULFILLED: {
      return {
        ...state,
        updateTemplateError: null,
        templates: state.templates.map((t) => (
          t.id === action.payload.id ? action.payload : t
        )),
      };
    }
    case UPDATE_TEMPLATE_REJECTED: {
      return { ...state, updateTemplateError: getErrorMessage(action.payload) };
    }
    case DELETE_TEMPLATE_FULFILLED: {
      return {
        ...state,
        templates: state.templates.filter((t) => t.id !== action.payload),
      };
    }
    case MARK_BOARD_AS_TEMPLATE: {
      return update(state, {
        leaderboards: {
          [action.payload.boardId]: {
            isTemplate: { $set: true },
            templateId: { $set: action.payload.templateId },
          },
        },
      });
    }
    case SET_LEADERBOARD_VIEW:
    case CLEAR_ALL_BOARDS: {
      return {
        ...state,
        leaderboards: action.type === CLEAR_ALL_BOARDS ? {} : state.leaderboards,
        leaderboardsReference: action.type === CLEAR_ALL_BOARDS ? {} : state.leaderboardsReference,
        leaderboardError: null,
        saveTemplateError: null,
        updateTemplateError: null,
      };
    }
    default: {
      return state;
    }
  }
}
